<template>
  <div class="sheet widget mt-4">
    <div class="widget-content" v-if="importing">
      <AlertBlock :text="$t('training.rncp.alert.importing')" type="success" />
    </div>
    <template v-if="hasBeenImportedOnce && data && Object.keys(data).length">
      <div class="widget-heading flex-column" v-if="data && Object.keys(data).length">
        <div class="sheet__heading">
          <span class="sheet__heading__label">Fiche {{ data.code }}</span>
          <span class="badge ms-2 me-2" :class="{ 'bg-success': data.active, 'bg-danger': !data.active }">{{
            data.active ? "Active" : "Inactive"
          }}</span>
          <TrainingRncpLevelComponent :level="data.level" v-if="data.level" />
          <h2 class="textStyle mt-3" v-if="data.name">
            {{ data.name }}
          </h2>
        </div>
      </div>
      <div class="widget-content">
        <div class="sheet__tags" v-if="data.degreeAbbreviation">{{ data.degreeAbbreviation }}</div>
        <div class="row mb-3">
          <div class="col-xl-4" v-if="data.recordingDate">
            <div class="sheet__date">
              <div class="sheet__date__label">Date de publication</div>
              <div class="sheet__date__date" v-if="data.recordingDate">
                {{ dateFormatter().format(data.recordingDate, "short") }}
              </div>
            </div>
          </div>

          <div class="col-xl-4 mt-3 mt-xl-0">
            <div class="sheet__date">
              <div class="sheet__date__label">{{ $t("training.rncp.due_date") }}</div>
              <div class="sheet__date__date" v-if="data.endDate">
                {{ dateFormatter().format(data.endDate, "short") }}
              </div>
            </div>
          </div>

          <div class="col-xl-4 mt-3 mt-xl-0">
            <AlertBlock
              v-if="isAlert"
              :title="alertTitle"
              :text="$t('training.rncp.alert.update_rncp')"
              type="warning mb-0"
              icon="triangle-exclamation"
            />
          </div>
        </div>

        <div
          class="sheet__certification mb-1"
          v-if="data.previousRncpCode && Object.keys(data.previousRncpCode).length"
        >
          {{ $t("training.rncp.previous_certification") }} :
          <span class="fw-bold textStyle">{{ data.previousRncpCode[0].code }}</span> ({{
            data.previousRncpCode[0].name
          }}) - {{ $t("training.rncp.new_certification") }} : <span class="fw-bold textStyle">{{ data.code }}</span> ({{
            data.name
          }})
        </div>

        <TrainingRncpAccordionComponent :data="data" />
      </div>
    </template>
    <div class="widget-content" v-else-if="!importing">
      <AlertBlock :text="$t('training.rncp.alert.rncp_sheet_import')" type="info" />
    </div>
    <BlockLoader :loading="importing || loading" />
  </div>
</template>

<script>
import AlertBlock from "@/components/blocks/AlertBlock.vue";
import TrainingRncpAccordionComponent from "@/components/training/rncp/TrainingRncpAccordionComponent.vue";
import { trainingManager } from "@/services/training/training-manager";
import BlockLoader from "@/components/blocks/BlockLoader.vue";
import { dateFormatter } from "@/services/utilities/date-formatter";
import { mapState } from "pinia";
import { useNotificationStore } from "@/store/notifications/notification";
import TrainingRncpLevelComponent from "@/components/training/rncp/TrainingRncpLevelComponent.vue";
import { i18n } from "@/i18n";

export default {
  name: "TrainingRncpSheetComponent",
  components: { TrainingRncpLevelComponent, BlockLoader, TrainingRncpAccordionComponent, AlertBlock },
  props: {
    hasBeenImportedOnce: {
      type: Boolean,
      required: true,
    },
    importing: {
      type: Boolean,
      required: true,
    },
    active: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      data: {},
      alertTitle: "",
      isAlert: false,
    };
  },
  computed: {
    ...mapState(useNotificationStore, {
      status: (store) => store.status,
      messages: (store) => store.messages,
    }),
  },
  watch: {
    status() {
      const messageId = sessionStorage.getItem(
        "studea-training-" + this.$route.params.trainingId + "-rncpImportMessageId",
      );
      const message = this.messages.find((message) => message.id === messageId);
      if (message) {
        if (message.status === "success") {
          this.init();
        }
      }
    },

    $route() {
      this.init();
    },
  },
  mounted() {
    this.init();
  },
  methods: {
    dateFormatter() {
      return dateFormatter;
    },
    init() {
      if (this.active) {
        this.getData();
      }
    },

    clearData() {
      this.data = {};
    },

    async getData() {
      this.loading = true;
      this.data = await trainingManager.get(this.$route.params.trainingId, "france-competence/");
      this.setAlert();
      this.loading = false;
    },

    setAlert() {
      let endDate = new Date(this.data.endDate);
      let ref = new Date();
      ref.setMonth(ref.getMonth() + 12);
      const monthCount = endDate.getMonth() - new Date().getMonth();
      let dayCount = null;
      if (monthCount <= 1) {
        dayCount = endDate.getDay() - new Date().getDate();
      }

      this.alertTitle = i18n.global.tc("training.rncp.alert.due_date_in_month", { month: monthCount });
      if (dayCount) {
        this.alertTitle = i18n.global.tc("training.rncp.alert.due_date_in_day", { day: dayCount });
      }

      this.isAlert = endDate < ref;
    },
  },
};
</script>

<style lang="scss" scoped>
.sheet {
  &__heading {
    &__label {
      text-transform: uppercase;
      color: var(--primary-color);
      font-weight: 600;

      @include dark-theme {
        color: $white;
      }
    }
  }

  &__tags {
    color: $m_color_7;
    margin-bottom: 25px;

    @include dark-theme {
      color: $m_color_1;
    }
  }

  &__date {
    &__label {
      color: $m_color_7;
      font-size: 14px;
      font-weight: 500;
      text-transform: uppercase;
      @include dark-theme {
        color: $m_color_1;
      }
    }
  }

  &__accordion {
    &__card + &__card {
      margin-top: 15px;
    }

    &__card {
      .card-header {
        border-bottom: none;
        padding: 20px;
      }
    }

    &__heading {
      display: flex;
      position: relative;
      font-weight: 700;
      color: var(--primary-color);

      .accordion-icon {
        margin-right: 8px;
        font-size: 16px;
      }

      .icons {
        position: absolute;
        right: 0;
        top: 50%;
        transform: translateY(-50%);
      }
    }
  }
}

.widget-content,
.widget-heading {
  @include up-to-md {
    padding: 15px 10px !important;
  }
}

.widget-content {
  padding-bottom: 0 !important;
}
</style>
